/**
 * Which views a view rule covers (spec 130, "View permissions"). Pure, so the Roles tab and the view
 * pages use it too. See docs/auth-permissions.md, "View rules".
 */
import picomatch from 'picomatch/posix';

/** A view as a rule sees it: its slug, its parent, and whether the person asking created it. */
export interface ViewTarget {
	slug: string;
	parent?: ViewTarget | null;
	/** The person asking created this view, so they hold every view permission on it (bounded by its parent for users). */
	mine?: boolean;
}

export const ALL_VIEWS = '*';

const MAX_VIEW_PATTERNS = 100;
const MAX_PATTERN_CHARS = 300;

/** Parent slugs down to this view, joined with `/`, e.g. `sales/leads-west`. */
export function viewPath(target: ViewTarget): string {
	const slugs: string[] = [];
	for (let t: ViewTarget | null | undefined = target; t; t = t.parent) slugs.unshift(t.slug);
	return slugs.join('/');
}

/**
 * Whether a rule pattern covers the view. `*` is every view. A pattern without `/` matches the slug,
 * which is unique per agent and survives moves. A pattern with `/` matches the path from any ancestor
 * down, so `sales/**` is sales and every view under it wherever sales sits.
 */
export function viewPatternMatches(pattern: string, target: ViewTarget): boolean {
	if (pattern === ALL_VIEWS || pattern === '**') return true;
	if (!pattern.includes('/')) return picomatch.isMatch(target.slug, pattern);
	const slugs = viewPath(target).split('/');
	return slugs.some((_, i) => picomatch.isMatch(slugs.slice(i).join('/'), pattern));
}

export function viewPatternsMatch(patterns: readonly string[] | undefined, target: ViewTarget): boolean {
	return !patterns || patterns.some((p) => viewPatternMatches(p, target));
}

/** Checks a rule's view patterns before they are saved. */
export function validateViewPatterns(value: unknown): { ok: true; views: string[] } | { ok: false; error: string } {
	if (!Array.isArray(value)) return { ok: false, error: 'views must be an array of view slugs or patterns' };
	if (value.length === 0) return { ok: false, error: 'views needs at least one view slug or pattern' };
	if (value.length > MAX_VIEW_PATTERNS) return { ok: false, error: `views can hold at most ${MAX_VIEW_PATTERNS} entries` };
	const views: string[] = [];
	for (const raw of value) {
		if (typeof raw !== 'string' || !raw.trim()) return { ok: false, error: 'Each view pattern must be a non-empty string' };
		const pattern = raw.trim().replace(/^\/+|\/+$/g, '');
		if (!pattern || pattern.length > MAX_PATTERN_CHARS) return { ok: false, error: `Not a valid view pattern: ${raw.slice(0, 40)}` };
		try {
			picomatch.makeRe(pattern);
		} catch {
			return { ok: false, error: `Not a valid view pattern: ${raw}` };
		}
		if (!views.includes(pattern)) views.push(pattern);
	}
	return { ok: true, views: views.includes(ALL_VIEWS) ? [ALL_VIEWS] : views };
}

/** Builds each view's target once, so parents are shared. `createdBy` is compared with `userId` for `mine`. */
export function viewTargets<V extends { id: number; slug: string; parentId: number | null; createdBy?: string | null }>(
	views: readonly V[],
	userId: string | null | undefined
): Map<number, ViewTarget> {
	const byId = new Map(views.map((v) => [v.id, v]));
	const out = new Map<number, ViewTarget>();
	const build = (view: V, seen: Set<number>): ViewTarget => {
		const done = out.get(view.id);
		if (done) return done;
		const parentRow = view.parentId !== null && !seen.has(view.parentId) ? byId.get(view.parentId) : undefined;
		seen.add(view.id);
		const target: ViewTarget = {
			slug: view.slug,
			parent: parentRow ? build(parentRow, seen) : null,
			mine: !!userId && !!view.createdBy && view.createdBy === userId,
		};
		out.set(view.id, target);
		return target;
	};
	for (const view of views) build(view, new Set());
	return out;
}

/** JSON with object keys sorted, so two filter lists compare by content, not key order. */
function canonicalJson(value: unknown): string {
	if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
	if (value && typeof value === 'object') {
		const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined);
		return `{${entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
	}
	return JSON.stringify(value ?? null);
}

/** Whether two filter settings differ, which changes the users a view's rules reach. */
export function filtersDiffer(
	before: { filters?: unknown; logicalOperator?: unknown } | null | undefined,
	after: { filters?: unknown; logicalOperator?: unknown } | null | undefined,
): boolean {
	return (
		canonicalJson(before?.filters ?? []) !== canonicalJson(after?.filters ?? []) ||
		(before?.logicalOperator ?? 'and') !== (after?.logicalOperator ?? 'and')
	);
}
